import React, { useMemo, useState } from 'react';
import {
  BUSINESS_CALCULATOR_FORMULA_STEPS,
  DEFAULT_BUSINESS_CALCULATOR_INPUTS,
  calculateBusinessCalculatorMetrics,
  formatMoney,
  formatPercent,
  formatQty,
  getInsightState,
} from './businessCalculatorLogic';
import BusinessCalculatorNumberField from './BusinessCalculatorNumberField';
import { formatTrustedMessage } from '../../utils/htmlSecurity';
import './BusinessCalculator.css';

const CalcRow = ({ label, value, tone = 'default', valueTone }) => {
  const resolvedValueTone = valueTone || tone;

  return (
    <div
      className={`bc-calc-row${
        tone === 'neutral' ? ' bc-calc-row--neutral' : tone === 'warn' ? ' bc-calc-row--warn' : ''
      }`}
    >
      <span className="bc-calc-label">{label}</span>
      <span
        className={`bc-calc-val${
          resolvedValueTone === 'neg'
            ? ' bc-calc-val--neg'
            : resolvedValueTone === 'dark'
              ? ' bc-calc-val--dark'
              : resolvedValueTone === 'amber'
                ? ' bc-calc-val--amber'
                : ''
        }`}
      >
        {value}
      </span>
    </div>
  );
};

const BusinessCalculator = () => {
  const [inputs, setInputs] = useState(DEFAULT_BUSINESS_CALCULATOR_INPUTS);
  const locale = 'ru-RU';
  const pcsUnit = 'шт';

  const metrics = useMemo(() => calculateBusinessCalculatorMetrics(inputs), [inputs]);
  const insight = useMemo(() => getInsightState(metrics), [metrics]);

  const insightMessage = useMemo(
    () => formatTrustedMessage(insight.message, insight.vars || {}),
    [insight],
  );

  const handleChange = (field, value) => {
    setInputs((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const competitorDiffLabel = useMemo(() => {
    const { competitorDiff } = metrics;
    const formatted = formatMoney(Math.abs(competitorDiff), locale);
    const sign = competitorDiff > 0 ? '+' : competitorDiff < 0 ? '−' : '';
    return competitorDiff === 0 ? formatted : `${sign}${formatted}`;
  }, [metrics, locale]);

  const competitorTone = metrics.competitorDiff < 0 ? 'warn' : 'default';
  const competitorValTone = metrics.competitorDiff > 0 ? 'default' : metrics.competitorDiff < 0 ? 'neg' : 'dark';

  const bepQtyDisplay =
    metrics.gross <= 0
      ? '∞'
      : formatQty(metrics.bepQty, pcsUnit, locale);

  const bepRubDisplay =
    metrics.gross <= 0
      ? 'Цена ниже себестоимости'
      : `${formatMoney(metrics.bepRub, locale)} ${'выручки'}`;

  return (
    <div className="bc-calculator">
      <div className="bc-legend">
        <div className="bc-legend-item">
          <span className="bc-legend-dot bc-legend-dot--input" />
          {'Вводите вы'}
        </div>
        <div className="bc-legend-item">
          <span className="bc-legend-dot bc-legend-dot--calc" />
          {'Считается автоматически'}
        </div>
      </div>

      <div className="bc-layout">
        <section className="bc-panel">
          <h2 className="bc-panel-title">{'Закупка и производство'}</h2>
          <BusinessCalculatorNumberField
            label={'Количество единиц в закупке'}
            subLabel={'шт'}
            note={'Сколько единиц товара покупаете за раз у поставщика'}
            value={inputs.qty}
            min={1}
            onChange={(value) => handleChange('qty', value)}
          />
          <BusinessCalculatorNumberField
            label={'Цена закупки / производства'}
            subLabel={'за шт'}
            unit={'₸'}
            note={'Стоимость одной единицы у поставщика'}
            value={inputs.purchase}
            min={0}
            onChange={(value) => handleChange('purchase', value)}
          />
          <BusinessCalculatorNumberField
            label={'Доставка от поставщика до вас'}
            subLabel={'весь объём'}
            unit={'₸'}
            note={'Стоимость за весь заказ, потом делится на количество единиц'}
            value={inputs.deliveryIn}
            min={0}
            onChange={(value) => handleChange('deliveryIn', value)}
          />
          <BusinessCalculatorNumberField
            label={'Упаковка'}
            subLabel={'весь объём'}
            unit={'₸'}
            note={'Стоимость упаковки на весь объём закупки'}
            value={inputs.packing}
            min={0}
            onChange={(value) => handleChange('packing', value)}
          />
          <div className="bc-sep" />
          <CalcRow label={'Себестоимость 1 единицы'} value={formatMoney(metrics.cogs, locale)} tone="neutral" />
        </section>

        <section className="bc-panel">
          <h2 className="bc-panel-title">{'Переменные расходы на продажу'}</h2>
          <BusinessCalculatorNumberField
            label={'Доставка до покупателя'}
            subLabel={'за шт'}
            unit={'₸'}
            note={'Стоимость доставки каждой проданной единицы'}
            value={inputs.deliveryOut}
            min={0}
            onChange={(value) => handleChange('deliveryOut', value)}
          />
          <BusinessCalculatorNumberField
            label={'Реклама на 1 продажу (CPL / CR)'}
            subLabel={'за шт'}
            unit={'₸'}
            note={'Рекламный бюджет ÷ количество продаж из рекламы. Если реклама не запущена — 0'}
            value={inputs.ads}
            min={0}
            onChange={(value) => handleChange('ads', value)}
          />
          <BusinessCalculatorNumberField
            label={'Налоги и прочие расходы'}
            subLabel={'% от выручки'}
            unit={'%'}
            note={'УСН «Доходы» — 6%. Если не зарегистрированы — уточните у бухгалтера'}
            value={inputs.tax}
            min={0}
            max={50}
            step={0.5}
            onChange={(value) => handleChange('tax', value)}
          />
          <BusinessCalculatorNumberField
            label={'Комиссия банка / эквайринг'}
            subLabel={'%'}
            unit={'%'}
            note={'Комиссия за приём оплаты картой'}
            value={inputs.bank}
            min={0}
            max={10}
            step={0.5}
            onChange={(value) => handleChange('bank', value)}
          />
          <div className="bc-sep" />
          <CalcRow label={'Итого переменных на 1 продажу'} value={formatMoney(metrics.varPer, locale)} tone="neutral" />
        </section>
      </div>

      <div className="bc-layout">
        <section className="bc-panel">
          <h2 className="bc-panel-title">{'Цена продажи'}</h2>
          <BusinessCalculatorNumberField
            label={'Средняя цена у конкурентов'}
            unit={'₸'}
            note={'Посмотрите цены 3–5 конкурентов, возьмите среднее'}
            value={inputs.competitor}
            min={0}
            onChange={(value) => handleChange('competitor', value)}
          />
          <BusinessCalculatorNumberField
            label={'Средний чек (или цена одной продажи)'}
            unit={'₸'}
            value={inputs.price}
            min={0}
            onChange={(value) => handleChange('price', value)}
          />
          <BusinessCalculatorNumberField
            label={'Скидка (если есть акция)'}
            unit={'%'}
            note={'Заполните только во время акции. Увидите, как скидка влияет на прибыль'}
            value={inputs.discount}
            min={0}
            max={90}
            onChange={(value) => handleChange('discount', value)}
          />
          <div className="bc-sep" />
          <CalcRow label={'Итоговая цена со скидкой'} value={formatMoney(metrics.finalPrice, locale)} tone="neutral" />
          <CalcRow
            label={'Разница с конкурентами'}
            value={competitorDiffLabel}
            tone={competitorTone}
            valueTone={competitorValTone}
          />
        </section>

        <section className="bc-panel bc-panel--stack">
          <h2 className="bc-panel-title">{'Прибыль с единицы товара'}</h2>
          <CalcRow
            label={'Валовая прибыль (до постоянных расходов)'}
            value={formatMoney(metrics.gross, locale)}
            tone={metrics.gross < 0 ? 'neg' : 'default'}
          />
          <CalcRow
            label={'Маржинальность'}
            value={formatPercent(metrics.margin)}
            tone="neutral"
            valueTone={metrics.margin < 0 ? 'neg' : 'dark'}
          />
          <CalcRow label={'Налог с продажи'} value={formatMoney(metrics.taxRub, locale)} tone="neutral" />
          <CalcRow label={'Банк. комиссия в деньгах'} value={formatMoney(metrics.bankRub, locale)} tone="neutral" />
          <CalcRow label={'Полная себестоимость продажи'} value={formatMoney(metrics.fullCost, locale)} tone="neutral" />
        </section>
      </div>

      <section className="bc-panel bc-panel--fixed">
        <h2 className="bc-panel-title">{'Постоянные расходы в месяц'}</h2>
        <div className="bc-layout bc-layout--inner">
          <div>
            <BusinessCalculatorNumberField
              label={'Аренда (офис, студия, склад)'}
              unit={'₸'}
              note={'Если работаете из дома — 0'}
              value={inputs.rent}
              min={0}
              onChange={(value) => handleChange('rent', value)}
            />
            <BusinessCalculatorNumberField
              label={'Зарплата себе (фикс)'}
              unit={'₸'}
              value={inputs.salaryOwn}
              min={0}
              onChange={(value) => handleChange('salaryOwn', value)}
            />
            <BusinessCalculatorNumberField
              label={'Зарплата сотрудникам (фикс)'}
              unit={'₸'}
              value={inputs.salaryStaff}
              min={0}
              onChange={(value) => handleChange('salaryStaff', value)}
            />
          </div>
          <div>
            <BusinessCalculatorNumberField
              label={'Сервисы, подписки, ПО'}
              unit={'₸'}
              value={inputs.services}
              min={0}
              onChange={(value) => handleChange('services', value)}
            />
            <BusinessCalculatorNumberField
              label={'Финансовый резерв'}
              unit={'₸'}
              note={'Рекомендуется откладывать 5–10% от выручки'}
              value={inputs.reserve}
              min={0}
              onChange={(value) => handleChange('reserve', value)}
            />
            <BusinessCalculatorNumberField
              label={'Прочие постоянные расходы'}
              unit={'₸'}
              value={inputs.otherFixed}
              min={0}
              onChange={(value) => handleChange('otherFixed', value)}
            />
          </div>
        </div>
        <div className="bc-sep" />
        <CalcRow label={'Итого постоянных расходов в месяц'} value={formatMoney(metrics.fixed, locale)} tone="neutral" />
      </section>

      <div className="bc-results-grid">
        <article className="bc-res-card bc-res-card--dark">
          <div className="bc-res-label">{'Точка безубыточности'}</div>
          <div className="bc-res-val">{bepQtyDisplay}</div>
          <div className="bc-res-sub">{bepRubDisplay}</div>
          <p className="bc-res-explain">{'Сколько нужно продать, чтобы покрыть все расходы и выйти в ноль. До этой цифры — убыток.'}</p>
        </article>
        <article className="bc-res-card bc-res-card--green">
          <div className="bc-res-label">{'Валовая прибыль с 1 единицы'}</div>
          <div
            className={`bc-res-val${metrics.gross < 0 ? ' bc-res-val--neg' : ''}`}
          >
            {formatMoney(metrics.gross, locale)}
          </div>
          <div className="bc-res-sub">
            {formatPercent(metrics.margin)} {'маржинальность'}
          </div>
          <p className="bc-res-explain">{'Сколько зарабатываете с каждой продажи до вычета постоянных расходов'}</p>
        </article>
        <article className="bc-res-card bc-res-card--amber">
          <div className="bc-res-label">{'Чистая прибыль с 1 единицы'}</div>
          <div className="bc-res-val">{formatMoney(metrics.gross, locale)}</div>
          <div className="bc-res-sub">{'после постоянных расходов'}</div>
          <p className="bc-res-explain">{'При условии, что все постоянные расходы уже покрыты продажами выше точки безубыточности'}</p>
        </article>
        <article className="bc-res-card bc-res-card--accent">
          <div className="bc-res-label">{'Полная себестоимость продажи'}</div>
          <div className="bc-res-val">{formatMoney(metrics.fullCost, locale)}</div>
          <div className="bc-res-sub">{'закупка + все переменные'}</div>
          <p className="bc-res-explain">{'Минимальная цена, ниже которой продавать нельзя — вы уже в убытке на каждой единице'}</p>
        </article>
      </div>

      <section className="bc-panel">
        <h2 className="bc-panel-title">{'Ваша цель'}</h2>
        <div className="bc-layout bc-layout--goal">
          <BusinessCalculatorNumberField
            label={'Желаемая чистая прибыль в месяц'}
            unit={'₸'}
            note={'Сколько хотите получать на руки после всех расходов и налогов'}
            value={inputs.goal}
            min={0}
            onChange={(value) => handleChange('goal', value)}
          />
          <div className="bc-goal-calc">
            <CalcRow
              label={'Нужно продать'}
              value={
                metrics.gross > 0
                  ? formatQty(metrics.goalQty, pcsUnit, locale)
                  : '—'
              }
            />
            <CalcRow
              label={'Это выручка'}
              value={metrics.gross > 0 ? formatMoney(metrics.goalRev, locale) : '—'}
              tone="neutral"
            />
            <CalcRow
              label={'Продаж в день (30 дн.)'}
              value={
                metrics.gross > 0
                  ? `${metrics.goalDaily.toFixed(1)} ${'шт/день'}`
                  : '—'
              }
              tone="neutral"
            />
          </div>
        </div>
        <div className="bc-sep" />
        <h3 className="bc-panel-title bc-panel-title--sub">{'Что будет, если сделать скидку'}</h3>
        <div className="bc-discount-grid">
          {metrics.discountScenarios.map(({ disc, gross: grossDisc, bepQty: bepDisc }) => (
            <div
              key={disc}
              className={`bc-discount-card${
                disc === 20 && grossDisc < 0 ? ' bc-discount-card--warn' : ''
              }`}
            >
              <span className="bc-calc-label">{`При скидке ${disc}%`}</span>
              <span
                className={`bc-calc-val bc-calc-val--block${
                  grossDisc < 0 ? ' bc-calc-val--neg' : ' bc-calc-val--amber'
                }`}
              >
                {formatMoney(grossDisc, locale)}
              </span>
              <span className="bc-discount-meta">
                {grossDisc > 0
                  ? `${'ТБУ:'} ${formatQty(bepDisc, pcsUnit, locale)}`
                  : 'убыток на каждой'}
              </span>
            </div>
          ))}
        </div>
        <p className="bc-discount-footnote">{'Показывает прибыль с 1 единицы и сколько нужно продать для точки безубыточности при каждой скидке'}</p>
      </section>

      <div
        className={`bc-insight bc-insight--${insight.variant}`}
        dangerouslySetInnerHTML={{ __html: insightMessage }}
      />

      <details className="bc-formula-details">
        <summary className="bc-formula-summary">{'Как считается — шаг за шагом'}</summary>
        <section className="bc-formula-panel">
        {BUSINESS_CALCULATOR_FORMULA_STEPS.map((step) => (
          <article key={step.marker} className="bc-formula-step">
            <div className="bc-formula-step-num">{step.marker}</div>
            <div>
              <div className="bc-formula-step-title">{step.title}</div>
              <div className="bc-formula-step-formula">{step.formula}</div>
              <div
                className="bc-formula-step-example"
                dangerouslySetInnerHTML={{
                  __html: step.example,
                }}
              />
            </div>
          </article>
        ))}
        </section>
      </details>

    </div>
  );
};

export default BusinessCalculator;
